import React, { useEffect, useState } from 'react';
import { useQuery } from '@common/hooks';
import { Row, Col, Select, InputNumber, Drawer, Button } from 'antd';
import { selectInputFilterHelper } from '@common/services';
import {
  CALENDAR_VIEW_FILTERS_QUERY,
  CalendarViewFiltersResponse,
  LOCALSTORAGE_SHIFT_CALENDAR_FILTER_KEY,
} from '@scheduling/graphql';
import { LoadingState, Icon } from '@common/components';
import Filters from 'works/legacy/lib/filters';

export type CalendarFilterType = {
  facilityIds: string[];
  unitIds: string[];
  roleIds: string[];
  timeOfDay: string;
  length: [number, number];
};

export function ShiftsCalendarFiltersDrawer({
  onApplyFilters,
  visible,
  onClose,
}: {
  onApplyFilters: (newFilters: CalendarFilterType) => void;
  visible: boolean;
  onClose: () => void;
}) {
  const [newFilters, setNewFilters] = useState(
    Filters.getFilters(LOCALSTORAGE_SHIFT_CALENDAR_FILTER_KEY)
  );

  const [facilityIds, setFacilityIds] = useState(
    newFilters['facilityIds'] || []
  );
  const [minLength, setMinLength] = useState(
    newFilters['length'] && newFilters['length'][0]
  );
  const [maxLength, setMaxLength] = useState(
    newFilters['length'] && newFilters['length'][1]
  );
  const [applyFiltersDisabled, setApplyFiltersDisabled] = useState(true);

  const { data, loading } = useQuery<CalendarViewFiltersResponse>(
    CALENDAR_VIEW_FILTERS_QUERY,
    {
      variables: {
        input: {
          scope: 'department',
        },
      },
    }
  );

  const unitIds = newFilters['unitIds'] || [];

  useEffect(() => {
    const filters = Filters.getFilters(LOCALSTORAGE_SHIFT_CALENDAR_FILTER_KEY);

    setNewFilters(filters);
    setFacilityIds(filters['facilityIds'] || []);
    setMinLength(filters['length'] && filters['length'][0]);
    setMaxLength(filters['length'] && filters['length'][1]);
    setApplyFiltersDisabled(true);
  }, [visible]);

  const onFacilityChange = (newFacilityIds: [string]) => {
    const units = data?.organizationUnits.filter(({ id }) =>
      unitIds.includes(id)
    );

    const filteredUnitIds = units
      ?.filter(({ organizationBusiness }) =>
        newFacilityIds.includes(organizationBusiness.id)
      )
      .map(({ id }) => id);

    onFilterChange({
      facilityIds: newFacilityIds,
      unitIds: filteredUnitIds,
    });

    setFacilityIds(newFacilityIds);
  };

  const onMinLengthChange = (value: string | number | undefined) => {
    setMinLength(value);
    onFilterChange({ length: [value, maxLength] });
  };

  const onMaxLengthChange = (value: string | number | undefined) => {
    setMaxLength(value);
    onFilterChange({ length: [minLength, value] });
  };

  const onClearFilters = () => {
    setApplyFiltersDisabled(false);
    setFacilityIds([]);
    setMinLength(null);
    setMaxLength(null);
    setNewFilters({});
  };

  const onFilterChange = (newFilter: object) => {
    setApplyFiltersDisabled(false);
    const filters = { ...newFilters, ...newFilter };
    setNewFilters(filters);
  };

  return (
    <>
      <Drawer
        className="ShiftCalendarFiltersDrawer"
        title="Filters"
        visible={visible}
        width={600}
        onClose={onClose}
        footer={
          <Row gutter={[8, 0]}>
            <Col>
              <Button size="large" type="link" onClick={onClearFilters}>
                Clear All Filters
              </Button>
            </Col>
            <Col>
              <Button
                size="large"
                type="primary"
                onClick={() => {
                  setApplyFiltersDisabled(true);
                  onApplyFilters(newFilters);
                }}
                disabled={applyFiltersDisabled}
              >
                Apply Filters
              </Button>
            </Col>
          </Row>
        }
      >
        {loading ? (
          <LoadingState />
        ) : (
          <div className="ShiftCalendarFilters">
            <Row>Facility</Row>
            <Row style={{ marginBottom: 24 }}>
              <Select
                id="facility-dropdown"
                mode="multiple"
                style={{ width: '100%' }}
                allowClear
                filterOption={selectInputFilterHelper}
                showSearch
                size="large"
                placeholder="Select"
                onChange={onFacilityChange}
                value={facilityIds}
                suffixIcon={<Icon icon="search" kind="light" size="sm" />}
                showArrow
              >
                {data?.organizationBusinesses.map(({ id, name }) => {
                  return (
                    <Select.Option key={id} value={id}>
                      {name}
                    </Select.Option>
                  );
                })}
              </Select>
            </Row>

            <Row>Unit</Row>
            <Row style={{ marginBottom: 24 }}>
              <Select
                id="unit-dropdown"
                mode="multiple"
                style={{ width: '100%' }}
                allowClear
                filterOption={selectInputFilterHelper}
                showSearch
                size="large"
                placeholder="Select"
                onChange={(value) => onFilterChange({ unitIds: value })}
                value={unitIds}
                disabled={facilityIds.length < 1}
                suffixIcon={<Icon icon="search" kind="light" size="sm" />}
                showArrow
              >
                {data?.organizationUnits
                  .filter(({ organizationBusiness }) => {
                    if (facilityIds.length > 0) {
                      return facilityIds.includes(organizationBusiness.id);
                    }

                    return true;
                  })
                  .map(({ id, name }) => {
                    return (
                      <Select.Option key={id} value={id}>
                        {name}
                      </Select.Option>
                    );
                  })}
              </Select>
            </Row>

            <Row gutter={[16, 0]}>
              <Col span={12}>
                <Row>Role</Row>
                <Row style={{ marginBottom: 24 }}>
                  <Select
                    id="role-dropdown"
                    mode="multiple"
                    style={{ width: '100%' }}
                    allowClear
                    filterOption={selectInputFilterHelper}
                    showSearch
                    size="large"
                    placeholder="Select"
                    onChange={(value) => onFilterChange({ roleIds: value })}
                    value={newFilters['roleIds']}
                    suffixIcon={<Icon icon="search" kind="light" size="sm" />}
                    showArrow
                  >
                    {data?.roles.map(({ id, abbreviation }) => {
                      return (
                        <Select.Option key={id} value={id}>
                          {abbreviation}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Row>
              </Col>
              <Col span={12}>
                <Row>Period</Row>
                <Row>
                  <Select
                    id="period-dropdown"
                    style={{ width: '100%' }}
                    allowClear
                    size="large"
                    placeholder="Select"
                    onChange={(value) => onFilterChange({ timeOfDay: value })}
                    value={newFilters['timeOfDay']}
                    options={[
                      { label: 'Day', value: 'day' },
                      { label: 'Night', value: 'night' },
                    ]}
                  />
                </Row>
              </Col>
            </Row>

            <Row gutter={[16, 0]}>
              <Col span={12}>
                <Row>Length (Hours)</Row>
                <Row gutter={[16, 0]}>
                  <Col span={12}>
                    <InputNumber
                      style={{ width: '100%' }}
                      size="large"
                      placeholder="Min"
                      min={0}
                      max={24}
                      precision={1}
                      onChange={(value) => onMinLengthChange(value)}
                      value={minLength}
                    />
                  </Col>
                  <Col span={12}>
                    <InputNumber
                      style={{ width: '100%' }}
                      size="large"
                      placeholder="Max"
                      min={0}
                      max={24}
                      precision={1}
                      onChange={(value) => onMaxLengthChange(value)}
                      value={maxLength}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        )}
      </Drawer>
      <style jsx>{`
        :global(
          .ShiftCalendarFilters .ant-select-multiple .ant-select-selection-item
        ) {
          height: inherit !important;
        }
        :global(.ShiftCalendarFiltersDrawer .ant-drawer-title) {
          line-height: 28px;
        }
      `}</style>
    </>
  );
}
